import {useEffect, useMemo} from "react";
import {
	ContactFormSelectedCampusHandlers,
	ContactFormSelectedCampuses,
} from "./contactFormUtils";
import useSelect from "@/app/_components/forms/useSelect";
import {Key} from "react-aria-components";
import {useCookieCampusId} from "@/app/_components/contexts/CampusCookieContext";

interface UseContactFormSelectedCampusesOptions {
	resetSelectionOnCampusChange?: boolean;
}

export type UseContactFormSelectedCampuses = ContactFormSelectedCampuses &
	ContactFormSelectedCampusHandlers;

const useContactFormSelectedCampuses: (
	options?: UseContactFormSelectedCampusesOptions
) => UseContactFormSelectedCampuses = ({
	resetSelectionOnCampusChange = true,
} = {}) => {
	// setup campus fields
	const campusId = useCookieCampusId();

	const [
		primaryCampus,
		{
			onSelectionChange: onPrimaryCampusChange,
			setSelectedItem: setPrimaryCampus,
		},
	] = useSelect({defaultItem: campusId});
	const [
		secondaryCampus,
		{
			onSelectionChange: onSecondaryCampusChange,
			setSelectedItem: setSecondaryCampus,
		},
	] = useSelect();

	useEffect(() => {
		if (resetSelectionOnCampusChange) {
			setPrimaryCampus(campusId);
			setSecondaryCampus("");
		}
	}, [
		campusId,
		resetSelectionOnCampusChange,
		setPrimaryCampus,
		setSecondaryCampus,
	]);

	const handlers = useMemo(() => {
		return {
			setPrimaryCampus: (newPrimaryCampus: Key) => {
				onPrimaryCampusChange(newPrimaryCampus);
				setSecondaryCampus((currentSecondaryCampus) => {
					if (currentSecondaryCampus === newPrimaryCampus) {
						return "";
					}
					return currentSecondaryCampus;
				});
			},
			setSecondaryCampus: (newSecondaryCampus: Key) => {
				if (newSecondaryCampus !== primaryCampus) {
					onSecondaryCampusChange(newSecondaryCampus);
				}
			},
			resetCampusSelection: () => {
				setPrimaryCampus("");
				setSecondaryCampus("");
			},
		};
	}, [
		primaryCampus,
		setPrimaryCampus,
		onPrimaryCampusChange,
		setSecondaryCampus,
		onSecondaryCampusChange,
	]);

	return {primaryCampus, secondaryCampus, ...handlers};
};

export default useContactFormSelectedCampuses;
